import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FindingCard } from 'components/FindingCard';
import { FindingCitation, FindingEvent } from 'types/findings';
import { streamFindings } from 'services/findingsService';
import './findings.css';

export interface FindingsProps {
  /**
   * Optional token override, primarily used by tests. In production the
   * token is injected by the auth provider at the app root.
   */
  token?: string;
  /**
   * Optional host override. See `getApiHost` in `constants/config`. Tests
   * pass `null` so the mocked global fetch is called with a relative path.
   */
  address?: string | null;
}

type StreamState = 'idle' | 'streaming' | 'done' | 'error';

/**
 * Renders the v2 findings SSE stream inside the plugin's DockablePane
 * host. Each finding shows its clickable citation chips (Story 183).
 */
export const Findings: FunctionComponent<FindingsProps> = ({ token, address = null }) => {
  const [searchParams] = useSearchParams();
  const [findings, setFindings] = useState<FindingEvent[]>([]);
  const [state, setState] = useState<StreamState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [schemaVersion, setSchemaVersion] = useState<string | null>(null);

  const handleCitationActivate = useCallback(
    (citation: FindingCitation, finding: FindingEvent) => {
      // Story 183 AC: "click emits UI event". Dispatch a bubbling
      // CustomEvent so DockablePane hosts and analytics listeners can
      // observe the interaction without importing this component.
      const event = new CustomEvent('bim-comply:citation-activate', {
        bubbles: true,
        detail: { citation, finding },
      });
      window.dispatchEvent(event);
    },
    []
  );

  useEffect(() => {
    const controller = new AbortController();
    const elementType = searchParams.get('elementType') || 'stairs';

    // The v2 SSE endpoint runs the deterministic rule executor server-side
    // against the ElementType-keyed fixture registered on the API (see
    // `IFindingStreamProducer`), so a client-side Content payload is only
    // required when overriding with a bespoke JSON blob via `?content=`.
    // See `useMessageHandler.ts` for the existing `?elementType=` fixture
    // convention that this page mirrors.
    const request = {
      Source: 'web-plugin',
      Validator: 'Default',
      Country: 'Ireland',
      Language: 'English (Ireland)',
      ElementType: elementType,
      Content: searchParams.get('content') || '{}',
    };

    // Reset UI when inputs change (token refresh, elementType switch).
    setFindings([]);
    setSchemaVersion(null);
    setErrorMessage(null);
    setState('streaming');

    streamFindings(
      request,
      token,
      address,
      {
        onSchema: (version) => setSchemaVersion(version),
        onFinding: (finding) => setFindings((prev) => [...prev, finding]),
        onDone: () => {
          if (!controller.signal.aborted) {
            setState('done');
          }
        },
        onError: (err) => {
          if (!controller.signal.aborted) {
            setErrorMessage(err.message);
            setState('error');
          }
        },
      },
      controller.signal
    ).catch((err: Error) => {
      if (controller.signal.aborted) {
        return;
      }
      setErrorMessage(err.message);
      setState('error');
    });

    // AbortController cleanup handles React 18 StrictMode's double-invoke:
    // the first effect is aborted before the second one runs, so the
    // server call is deduplicated by the network stack rather than by a
    // ref guard. That also means token / searchParams changes correctly
    // trigger a fresh stream.
    return () => {
      controller.abort();
    };
  }, [searchParams, token, address]);

  return (
    <section className="findings-panel" data-testid="findings-panel" data-state={state}>
      <header className="findings-panel__header">
        <h1>Compliance findings</h1>
        {schemaVersion && (
          <span className="findings-panel__schema" data-testid="findings-schema-version">
            schema {schemaVersion}
          </span>
        )}
      </header>

      {state === 'streaming' && findings.length === 0 && (
        <p className="findings-panel__loading">Loading findings…</p>
      )}

      {state === 'error' && (
        <p className="findings-panel__error" role="alert">
          Failed to load findings: {errorMessage}
        </p>
      )}

      {state === 'done' && findings.length === 0 && (
        <p className="findings-panel__empty">No findings for this element.</p>
      )}

      <ol className="findings-panel__list">
        {findings.map((finding) => (
          <li key={finding.id} className="findings-panel__list-item">
            <FindingCard
              finding={finding}
              onCitationActivate={handleCitationActivate}
              token={token}
              address={address}
            />
          </li>
        ))}
      </ol>
    </section>
  );
};

export default Findings;
